import { useCallback, useEffect, useMemo, useState } from "react";
import RbacService, { Permission, Role } from "@/app/_services/RbacService";
import { toast } from "@/app/_utils/toast";

/** Groups permissions for the checkbox grid: pages first, then actions by domain. */
export function groupPermissions(permissions: Permission[]) {
  const groups = new Map<string, Permission[]>();
  permissions.forEach((permission) => {
    const key =
      permission.kind === "page"
        ? "Pages"
        : permission.codename.split(":")[0].replace(/^\w/, (c) => c.toUpperCase());
    const bucket = groups.get(key) ?? [];
    bucket.push(permission);
    groups.set(key, bucket);
  });
  return Array.from(groups.entries()).sort(([a], [b]) =>
    a === "Pages" ? -1 : b === "Pages" ? 1 : a.localeCompare(b)
  );
}

export default function useRolesModel() {
  const [roles, setRoles] = useState<Role[]>([]);
  const [permissions, setPermissions] = useState<Permission[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<Role | null>(null);
  const [search, setSearch] = useState("");

  const load = useCallback(async () => {
    setIsLoading(true);
    try {
      const [nextRoles, nextPermissions] = await Promise.all([
        RbacService.getRolesAsync(),
        RbacService.getPermissionsAsync(),
      ]);
      setRoles(nextRoles);
      setPermissions(nextPermissions);
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not load roles");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const grouped = useMemo(() => groupPermissions(permissions), [permissions]);

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return roles;
    return roles.filter(
      (role) =>
        role.name.toLowerCase().includes(query) ||
        (role.description ?? "").toLowerCase().includes(query)
    );
  }, [roles, search]);

  async function deleteRole(role: Role) {
    try {
      await RbacService.deleteRoleAsync(role.id);
      toast.success(`${role.name} deleted`);
      await load();
    } catch (e) {
      toast.error(e instanceof Error ? e.message : "Could not delete the role");
    }
  }

  return {
    roles: filtered,
    permissions,
    groupedPermissions: grouped,
    isLoading,
    search,
    setSearch,
    dialogOpen,
    setDialogOpen,
    editing,
    setEditing,
    reload: load,
    deleteRole,
  };
}
